/**
 * Application roles. Exactly two, per the school's policy.
 * Guardians, students, teachers, cashiers, bursars etc. are NOT application roles.
 */
export const ROLES = ['administrative', 'accounts_office'] as const;
export type AppRole = (typeof ROLES)[number];

export const ROLE_LABELS: Record<AppRole, string> = {
  administrative: 'Administrative',
  accounts_office: 'Accounts Office',
};

/** Capability identifiers used by the central permission policy. */
export type Capability =
  | 'dashboard:view'
  | 'dashboard:view_financial'
  | 'student:view'
  | 'student:create'
  | 'student:update'
  | 'student:archive'
  | 'student:view_notes'
  | 'guardian:view'
  | 'guardian:manage'
  | 'document:view'
  | 'document:upload'
  | 'document:delete'
  | 'academic:view'
  | 'academic:manage'
  | 'year:close'
  | 'fees:view'
  | 'fees:manage'
  | 'invoice:view'
  | 'invoice:create'
  | 'invoice:void'
  | 'payment:view'
  | 'payment:record'
  | 'payment:reverse'
  | 'receipt:view'
  | 'receipt:issue'
  | 'account:view'
  | 'account:statement'
  | 'report:view'
  | 'report:export'
  | 'user:view'
  | 'user:create'
  | 'user:update'
  | 'user:suspend'
  | 'user:reset_access'
  | 'approval:view'
  | 'approval:request'
  | 'approval:decide'
  | 'audit:view'
  | 'settings:view'
  | 'settings:manage'
  | 'backup:view'
  | 'backup:run';

/**
 * Central permission policy. The server enforces this on every protected
 * endpoint; the UI only uses it to decide what to render.
 */
const ACCOUNTS_CAPABILITIES: Capability[] = [
  'dashboard:view',
  'dashboard:view_financial',
  'student:view',
  'student:create',
  'student:update',
  'guardian:view',
  'guardian:manage',
  'document:view',
  'document:upload',
  'academic:view',
  'fees:view',
  'invoice:view',
  'invoice:create',
  'payment:view',
  'payment:record',
  'receipt:view',
  'receipt:issue',
  'account:view',
  'account:statement',
  'report:view',
  'report:export',
  'approval:view',
  'approval:request',
];

const ADMINISTRATIVE_CAPABILITIES: Capability[] = [
  'dashboard:view',
  'dashboard:view_financial',
  'student:view',
  'student:create',
  'student:update',
  'student:archive',
  'student:view_notes',
  'guardian:view',
  'guardian:manage',
  'document:view',
  'document:upload',
  'document:delete',
  'academic:view',
  'academic:manage',
  // Closing a financial year is a governance action, so it stays with the
  // administrative role even though Accounts sees every balance involved.
  'year:close',
  'fees:view',
  'fees:manage',
  'invoice:view',
  'invoice:create',
  'invoice:void',
  'payment:view',
  'payment:record',
  'payment:reverse',
  'receipt:view',
  'receipt:issue',
  'account:view',
  'account:statement',
  'report:view',
  'report:export',
  'user:view',
  'user:create',
  'user:update',
  'user:suspend',
  'user:reset_access',
  'approval:view',
  'approval:request',
  'approval:decide',
  'audit:view',
  'settings:view',
  'settings:manage',
  'backup:view',
  'backup:run',
];

export const PERMISSION_POLICY: Record<AppRole, readonly Capability[]> = {
  administrative: ADMINISTRATIVE_CAPABILITIES,
  accounts_office: ACCOUNTS_CAPABILITIES,
};

export function can(role: AppRole | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  return PERMISSION_POLICY[role].includes(capability);
}

/** Actions that must never be self-approved (separation of duties). */
export const SELF_APPROVAL_FORBIDDEN_TYPES = [
  'user_access',
  'refund',
  'invoice_correction',
  'payment_reversal',
  'fee_structure_change',
] as const;
